<template>
  <v-container fluid class="py-4 px-4">
    <!-- Toolbar -->
    <v-app-bar app :color="isDark ? '#2A2A2A' : 'primary'" :dark="isDark">
      <!-- Back Button -->
      <v-btn icon @click="goToProjectsList" class="back-btn">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <v-toolbar-title>
        <v-icon icon="mdi-book-open-page-variant"></v-icon> &nbsp; Documents List in: <strong>{{ project.name }}</strong>
      </v-toolbar-title>

      <v-spacer></v-spacer>
      <v-btn v-if="canManageDocuments" color="white" class="toolbar-btn" @click="goToProjectDashboard">
        <v-icon>mdi-chart-box</v-icon>
        &nbsp; Project Dashboard
</v-btn>

      <v-menu>
  <template v-slot:activator="{ props }">
    <v-btn
      v-if="canManageDocuments"
      color="white"
      class="toolbar-btn"
      v-bind="props"
    >
    <v-icon>mdi-checkbox-multiple-marked-outline</v-icon>
    &nbsp; Manage Selected
    </v-btn>
  </template>
  <v-list>
    <v-list-item
      :disabled="selectedDocuments.length !== 1"
      @click="handleEdit"
    >
      <v-list-item-title>Edit</v-list-item-title>
    </v-list-item>
    <v-list-item
      :disabled="selectedDocuments.length === 0"
      @click="handleDelete"
    >
      <v-list-item-title>Delete</v-list-item-title>
    </v-list-item>
    <v-list-item
      :disabled="selectedDocuments.length === 0"
      @click="openAssignDocumentDialog"
    >
      <v-list-item-title>Assign to</v-list-item-title>
    </v-list-item>
  </v-list>
</v-menu>
      <!-- Assignments Button -->
      <v-btn v-if="canManageDocuments" color="white" class="toolbar-btn" @click="openAssignmentsDialog">
        <v-icon>mdi-account-arrow-right</v-icon>
        &nbsp; Assignments
      </v-btn>
      <!-- Members Button -->
      <v-btn v-if="canManageDocuments" color="white" class="toolbar-btn" @click="openMembersDialog">
        <v-icon>mdi-account-group</v-icon>
        &nbsp; Members
      </v-btn>
      <!-- Add Document Button -->
      <v-btn v-if="canManageDocuments" color="white" class="toolbar-btn" @click="openCreateDocumentDialog">
        <v-icon>mdi-file-plus</v-icon>
    &nbsp; Add Document
      </v-btn>
      <v-btn color="white" class="toolbar-btn" @click="openImportDocumentDialog">
        <v-icon>mdi-file-import-outline</v-icon>
        &nbsp; Import Document
      </v-btn>
    </v-app-bar>

    <!-- Loading Spinner -->
    <v-row justify="center" align="center" v-if="loading" class="my-8">
      <v-progress-circular indeterminate color="primary" size="70"></v-progress-circular>
    </v-row>

    <!-- Documents Table -->
    <v-card flat>
      <div v-if="documents.length > 0">
      <v-card-title class="d-flex align-center pe-2">

        <!-- Search Field -->
        <v-text-field
          v-model="search"
          density="compact"
          label="Search"
          prepend-inner-icon="mdi-magnify"
          variant="solo-filled"
          flat
          hide-details
          single-line
        ></v-text-field>
      </v-card-title>

      <v-divider></v-divider>

      <v-data-table
        v-model:search="search"
        v-model:sort-by="sortBy"
        :headers="headers"
        :items="documents"
        item-value="id"
        class="elevation-2"
        dense
        hide-default-footer
      >
      <!-- Custom sortable header for Created On -->
      <template v-slot:header.created_at="{ column }">
        <div class="sortable-header" @click="toggleDateSort" role="button" :aria-label="`Sort by Created On ${currentDateSort === 'asc' ? 'descending' : 'ascending'}`">
          <span>Created On</span>
          <v-icon size="16" class="ms-1 sort-icon" v-if="currentDateSort === 'asc'">mdi-arrow-up</v-icon>
          <v-icon size="16" class="ms-1 sort-icon" v-else>mdi-arrow-down</v-icon>
        </div>
      </template>
      <template v-slot:header.select="{ }">
    <v-checkbox
      :indeterminate="isSomeSelected"
      :input-value="isAllSelected"
      @click.stop="toggleSelectAll"
      hide-details
      density="compact"
    ></v-checkbox>
  </template>
        <!-- Custom Body for Actions -->
        <template v-slot:body="{ items }">
          <tr
            v-for="document in items"
            :key="document.id"
            @click="goToDocument(document.id)"
            class="clickable-row"
          >
          <td>
            <v-checkbox
  v-model="selectedDocuments"
  :value="document.id"
  hide-details
  density="compact"
  @click.stop
></v-checkbox>

    </td>
            <td>{{ document.name }}</td>
            <td class="text-center">{{ formatDate(document.created_at) }}</td>
            <td class="text-center">
              <div class="status-rectangles">
  <v-tooltip
    v-for="(count, status) in document.lineStatus"
    :key="status"
    :text="getStatusTooltip(status)"
    location="bottom"
    open-on-hover
  >
    <template v-slot:activator="{ props }">
      <div
        class="status-rectangle"
        v-bind="props"
        :style="{ backgroundColor: getStatusColor(status) }"
      >
        {{ count }}
      </div>
    </template>
  </v-tooltip>
</div>



            </td>
            <td class="text-center">
              <v-tooltip
                :text="document.origin_url ? 'View in eScriptorium' : 'Source unavailable'"
                location="bottom"
              >
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    size="small"
                    :disabled="!document.origin_url"
                    :href="document.origin_url"
                    target="_blank"
                    @click.stop
                    class="source-btn"
                  >
                    <img
                      :src="require('@/assets/escriptorium.png')"
                      width="20"
                      height="20"
                      :style="{ opacity: document.origin_url ? 1 : 0.3 }"
                    />
                  </v-btn>
                </template>
              </v-tooltip>
            </td>
            <td class="text-end actions-cell" v-if="canManageDocuments">
              <v-tooltip text="Edit" location="bottom">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="action-btn edit"
                    color="primary"
                    variant="flat"
                    size="small"
                    icon
                    @click.stop="openEditDocumentDialog(document)"
                  >
                    <v-icon size="18">mdi-pencil</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
              <v-tooltip text="Delete" location="bottom">
                <template v-slot:activator="{ props }">
                  <v-btn
                    v-bind="props"
                    class="action-btn delete"
                    color="error"
                    variant="flat"
                    size="small"
                    icon
                    @click.stop="deleteDocument(document.id)"
                  >
                    <v-icon size="18">mdi-delete</v-icon>
                  </v-btn>
                </template>
              </v-tooltip>
            </td>
          </tr>
        </template>
      </v-data-table>
      </div>
      <!-- Empty State Message -->
      <div v-if="!loading && documents.length === 0" class="empty-state">
        <v-container class="text-center">
          <v-icon size="64" :color="isDark ? 'grey lighten-1' : 'grey darken-1'">mdi-emoticon-sad-outline</v-icon>
          <p class="empty-message">No documents found. Why not add one? 😊</p>
        </v-container>
      </div>
    </v-card>
<v-dialog v-model="importDocumentDialog" max-width="900px" @keydown.esc="importDocumentDialog = false">
  <escriptorium-browser
    mode="documents"
    :baseURL="escriptoriumBaseURL"
    :apiToken="importDocForm.apiToken"
    :saveToken="saveApiToken"
    @update:apiToken="val => importDocForm.apiToken = val"
    @update:saveToken="val => saveApiToken = val"
    @close="importDocumentDialog = false"
    @select-document="onImportDocument"
    @select-documents="onImportDocuments"
  />
</v-dialog>
    <!-- Create Document Dialog -->
    <v-dialog v-model="createDocumentDialog" max-width="500px" @keydown.enter="formValid && createDocument()" @keydown.esc="closeDialogs">
      <v-card>
        <v-card-title>
          <span class="text-h5">Create New Document</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="documentForm" v-model="formValid">
            <v-text-field
              v-model="documentFormData.name"
              label="Document Name"
              :rules="[v => !!v || 'Document name is required']"
              required
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!formValid" @click="createDocument">Create</v-btn>
          <v-btn text @click="closeDialogs">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Edit Document Dialog -->
    <v-dialog v-model="editDocumentDialog" max-width="500px" @keydown.enter="formValid && updateDocument()" @keydown.esc="closeDialogs">
      <v-card>
        <v-card-title>
          <span class="text-h5">Edit Document</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="documentForm" v-model="formValid">
            <v-text-field
              v-model="documentFormData.name"
              label="Document Name"
              :rules="[v => !!v || 'Document name is required']"
              required
            ></v-text-field>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" :disabled="!formValid" @click="updateDocument">Save</v-btn>
          <v-btn text @click="closeDialogs">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Members Dialog -->
    <v-dialog v-model="membersDialog" max-width="640px" @keydown.esc="membersDialog = false">
      <v-card>
        <v-card-title class="d-flex align-center">
          <v-icon class="me-2" color="primary">mdi-account-group</v-icon>
          <span class="text-h5">Project Members</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4" style="max-height: 420px; overflow-y: auto;">
          <div v-if="membersLoading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="projectMembers.length > 0">
            <v-list class="py-0">
              <v-list-item
                v-for="member in projectMembers"
                :key="member.id"
                class="member-item"
              >
                <template v-slot:prepend>
                  <v-avatar size="48" class="me-3">
                    <img
                      v-if="member.image"
                      :src="member.image"
                      alt="Member avatar"
                      class="circle-image"
                    />
                    <v-icon v-else>mdi-account-circle</v-icon>
                  </v-avatar>
                </template>
                <v-list-item-title class="font-weight-medium">{{ member.username }}</v-list-item-title>
                <v-list-item-subtitle>
                  <v-select
                    v-if="canManageDocuments"
                    v-model="member.primaryRole"
                    :items="memberRoleItems"
                    item-title="title"
                    item-value="name"
                    density="compact"
                    variant="outlined"
                    hide-details
                    class="member-role-select mt-1"
                    :loading="member.roleUpdating"
                    @update:modelValue="val => updateMemberRole(member, val)"
                    @click.stop
                  ></v-select>
                  <v-chip v-else size="small" :color="memberRoleColor(member.primaryRole)" variant="tonal" class="mt-1">
                    {{ memberRoleName(member.primaryRole) }}
                  </v-chip>
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </div>
          <div v-else class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1">mdi-account-group-outline</v-icon>
            <p class="text-grey mt-3">No members in this project</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="membersDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assign Document Dialog -->
    <v-dialog v-model="assignDocumentDialog" max-width="500px" @keydown.enter="assignDocumentCollaborators.length > 0 && confirmAssignDocument()" @keydown.esc="assignDocumentDialog = false">
      <v-card>
        <v-card-title>
          <span class="headline">Assign Document(s) to Collaborators</span>
        </v-card-title>
        <v-card-text>
          <v-autocomplete
            v-model="assignDocumentCollaborators"
            :items="projectCollaborators"
            item-title="username"
            item-value="id"
            label="Collaborators"
            chips
            multiple
            outlined
          ></v-autocomplete>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="confirmAssignDocument">Assign</v-btn>
          <v-btn color="red darken-1" text @click="assignDocumentDialog = false">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Assignments Overlay -->
    <v-dialog v-model="assignmentsDialog" max-width="900px" @keydown.esc="assignmentsDialog = false">
      <v-card>
        <v-card-title class="d-flex align-center flex-wrap">
          <v-icon class="me-2" color="primary">mdi-account-arrow-right</v-icon>
          <span class="text-h5">Assignments for {{ project.name }}</span>
          <v-spacer></v-spacer>
          <div class="legend">
            <span class="legend-item"><span class="legend-dot" style="background:#E57373"></span>Not transcribed</span>
            <span class="legend-item"><span class="legend-dot" style="background:#FDD835"></span>Transcribed</span>
            <span class="legend-item"><span class="legend-dot" style="background:#66BB6A"></span>Validated</span>
          </div>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-4" style="max-height: 580px; overflow-y: auto;">
          <div v-if="assignmentsLoading" class="text-center py-6">
            <v-progress-circular indeterminate color="primary"></v-progress-circular>
          </div>
          <div v-else-if="assignmentUsers.length > 0">
            <v-card
              v-for="u in assignmentUsers"
              :key="u.id"
              class="assignment-user pa-3 mb-3"
              variant="outlined"
            >
              <div class="d-flex align-center mb-2">
                <v-avatar size="36" class="me-2">
                  <img v-if="u.image" :src="u.image" class="circle-image" alt="avatar" />
                  <v-icon v-else>mdi-account-circle</v-icon>
                </v-avatar>
                <div>
                  <div class="font-weight-medium">{{ u.username }}</div>
                  <div class="text-caption text-medium-emphasis">{{ (u.roles || []).join(', ') || 'no role' }}</div>
                </div>
                <v-spacer></v-spacer>
                <div class="status-rectangles">
                  <v-tooltip text="Lines not transcribed" location="bottom">
                    <template v-slot:activator="{ props }">
                      <div class="status-rectangle" v-bind="props" style="background-color:#E57373">
                        {{ u.line_status_summary.not_transcribed }}
                      </div>
                    </template>
                  </v-tooltip>
                  <v-tooltip text="Lines transcribed (to validate)" location="bottom">
                    <template v-slot:activator="{ props }">
                      <div class="status-rectangle" v-bind="props" style="background-color:#FDD835">
                        {{ u.line_status_summary.transcribed }}
                      </div>
                    </template>
                  </v-tooltip>
                  <v-tooltip text="Lines validated" location="bottom">
                    <template v-slot:activator="{ props }">
                      <div class="status-rectangle" v-bind="props" style="background-color:#66BB6A">
                        {{ u.line_status_summary.validated }}
                      </div>
                    </template>
                  </v-tooltip>
                </div>
              </div>

              <!-- Current assignments -->
              <div class="d-flex align-center flex-wrap assignment-chips">
                <v-chip
                  v-for="docId in u.document_ids"
                  :key="`doc-${docId}`"
                  color="primary"
                  size="small"
                  class="ma-1"
                  closable
                  @click:close="removeAssignedDocument(u, docId)"
                >
                  <v-icon start size="14">mdi-file-document</v-icon>
                  {{ documentNameById(docId) }}
                </v-chip>
                <v-chip
                  v-for="page in u.pages"
                  :key="`page-${page.id}`"
                  size="small"
                  class="ma-1"
                  closable
                  @click:close="removeAssignedPage(u, page.id)"
                >
                  {{ documentNameById(page.document_id) }} (p. {{ page.number }})
                </v-chip>
                <span
                  v-if="u.document_ids.length === 0 && u.pages.length === 0"
                  class="text-caption text-medium-emphasis ma-1"
                >
                  Nothing assigned
                </span>
              </div>

              <!-- Add assignments -->
              <div class="d-flex align-center flex-wrap mt-2">
                <v-autocomplete
                  v-model="u.newDocumentId"
                  :items="assignmentDocuments"
                  item-title="name"
                  item-value="id"
                  label="Document"
                  density="compact"
                  variant="outlined"
                  hide-details
                  clearable
                  class="me-2 assignment-add-select"
                  @update:modelValue="u.newPageIds = []"
                ></v-autocomplete>
                <v-autocomplete
                  v-model="u.newPageIds"
                  :items="availablePagesFor(u)"
                  item-title="label"
                  item-value="id"
                  label="Pages (empty = whole document)"
                  density="compact"
                  variant="outlined"
                  hide-details
                  chips
                  multiple
                  clearable
                  :disabled="!u.newDocumentId"
                  class="me-2 assignment-add-select"
                ></v-autocomplete>
                <v-btn
                  color="primary"
                  size="small"
                  :disabled="!u.newDocumentId"
                  :loading="u.updating"
                  @click="addAssignment(u)"
                >
                  Assign
                </v-btn>
              </div>
            </v-card>
          </div>
          <div v-else class="text-center py-8">
            <v-icon size="64" color="grey-lighten-1">mdi-account-group-outline</v-icon>
            <p class="text-grey mt-3">No collaborators in this project</p>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn @click="assignmentsDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import api from "@/plugins/axios";
import EscriptoriumBrowser from "@/components/EscriptoriumBrowser.vue";

export default {
  components: { EscriptoriumBrowser },
  data() {
    return {
      importDocumentDialog: false,
      escriptoriumBaseURL: process.env.VUE_APP_ESCRIPTORIUM_BASE_URL,
      importDocForm: { apiToken: '' },
      saveApiToken: false,
      project: {},
      selectedDocuments: [],
      documents: [],
      search: "", // Search field binding
      loading: true,
      formValid: false,
      createDocumentDialog: false,
      editDocumentDialog: false,
      documentFormData: {
        id: null,
        name: "",
      },
      sortBy: [],
      currentDateSort: 'desc',
      membersDialog: false,
      membersLoading: false,
      projectMembers: [],
      memberRoleItems: [
        { title: 'Transcriber', name: 'transcriber' },
        { title: 'Project Leader', name: 'project_leader' },
        { title: 'Administrator', name: 'administrator' },
      ],
      assignDocumentDialog: false,
      assignDocumentCollaborators: [],
      projectCollaborators: [],
      assignmentsDialog: false,
      assignmentsLoading: false,
      assignmentUsers: [],
      assignmentDocuments: [],
      baseHeaders: [
      { title: "", align: "center", value: "select", sortable: false }, // Checkbox column
        { title: "Document Name", align: "start", value: "name" },
        { title: "Created On", align: "center", value: "created_at", sortable: true },
        { title: "Status", align: "center", value: "status", sortable: false },
        { title: "Source", align: "center", value: "origin_url", sortable: false },
      ],
    };
  },
  mounted() {
    this.fetchProject();
    this.fetchDocuments();
    const cached = localStorage.getItem('prefs.escriptoriumToken')
    if (cached) {
      this.importDocForm.apiToken = cached
      this.saveApiToken = true
    }
  },
  computed: {
    isDark() {
      return this.$vuetify.theme.global.current.dark;
    },
    canManageDocuments() {
      const userRoles = JSON.parse(localStorage.getItem("roles") || "[]");
      return userRoles.includes("administrator") || userRoles.includes("project_leader");
    },
    headers() {
      return this.canManageDocuments
        ? [...this.baseHeaders, { title: "Actions", align: "end", value: "actions", sortable: false }]
        : this.baseHeaders;
    },
    isAllSelected() {
      // True if we have items and the number of selected docs = total docs
      return (
        this.documents.length > 0 &&
        this.selectedDocuments.length === this.documents.length
      );
    },
    isSomeSelected() {
      // True if at least one is selected, but not all
      return (
        this.selectedDocuments.length > 0 &&
        this.selectedDocuments.length < this.documents.length
      );
    },
  },
  methods: {
    memberRoleColor(role) {
      const map = { administrator: 'error', project_leader: 'primary', transcriber: 'success' };
      return map[role] || 'grey';
    },
    memberRoleName(role) {
      const item = this.memberRoleItems.find(r => r.name === role);
      return item ? item.title : (role || 'No role');
    },
    async openMembersDialog() {
      this.membersDialog = true;
      this.membersLoading = true;
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.get(`/api/projects/${projectId}/collaborators/`);
        this.projectMembers = response.data.map(m => ({
          ...m,
          primaryRole: (m.roles && m.roles[0]) || 'transcriber',
          roleUpdating: false,
        }));
      } catch (error) {
        console.error("Error fetching members:", error);
      } finally {
        this.membersLoading = false;
      }
    },
    async updateMemberRole(member, newRole) {
      const projectId = this.$route.params.projectId;
      member.roleUpdating = true;
      try {
        await api.patch(`/api/projects/${projectId}/update-member-role/`, {
          user_id: member.id,
          role: newRole,
        });
      } catch (error) {
        console.error("Error updating role:", error);
        member.primaryRole = (member.roles && member.roles[0]) || 'transcriber';
      } finally {
        member.roleUpdating = false;
      }
    },
    async openAssignDocumentDialog() {
      const projectId = this.$route.params.projectId;
      try {
        const response = await api.get(`/api/projects/${projectId}/collaborators/`);
        this.projectCollaborators = response.data;
        // Pre-select collaborators already assigned to the first selected document (if one selected)
        if (this.selectedDocuments.length === 1) {
          const doc = this.documents.find(d => d.id === this.selectedDocuments[0]);
          this.assignDocumentCollaborators = doc ? (doc.assigned_user_ids || []) : [];
        } else {
          this.assignDocumentCollaborators = [];
        }
      } catch (error) {
        console.error("Error fetching collaborators:", error);
        this.projectCollaborators = [];
        this.assignDocumentCollaborators = [];
      }
      this.assignDocumentDialog = true;
    },
    async confirmAssignDocument() {
      const projectId = this.$route.params.projectId;
      try {
        // additive, adds the selected documents without clearing other users existing assignments
        for (const userId of this.assignDocumentCollaborators) {
          await api.post(`/api/projects/${projectId}/update_assignments/`, {
            user_id: userId,
            add_document_ids: this.selectedDocuments,
          });
        }
        for (const docId of this.selectedDocuments) {
          const doc = this.documents.find(d => d.id === docId);
          if (doc) {
            doc.assigned_user_ids = [
              ...new Set([...(doc.assigned_user_ids || []), ...this.assignDocumentCollaborators]),
            ];
          }
        }
        this.assignDocumentDialog = false;
        this.assignDocumentCollaborators = [];
      } catch (error) {
        console.error("Error assigning document:", error);
      }
    },
    documentNameById(docId) {
      const doc = this.assignmentDocuments.find((d) => d.id === docId);
      return doc ? doc.name : `Document ${docId}`;
    },
    availablePagesFor(user) {
      const doc = this.assignmentDocuments.find((d) => d.id === user.newDocumentId);
      if (!doc) return [];
      const assignedPageIds = user.pages.map((p) => p.id);
      return doc.pages
        .filter((p) => !assignedPageIds.includes(p.id))
        .map((p) => ({ id: p.id, label: `Page ${p.number}` }));
    },
    openAssignmentsDialog() {
      this.assignmentsDialog = true;
      this.fetchAssignments();
    },
    async fetchAssignments() {
      this.assignmentsLoading = true;
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.get(`/api/projects/${projectId}/assignments/`);
        this.assignmentDocuments = response.data.documents;
        this.assignmentUsers = response.data.users.map((u) => ({
          ...u,
          newDocumentId: null,
          newPageIds: [],
          updating: false,
        }));
      } catch (error) {
        console.error("Error fetching assignments:", error);
      } finally {
        this.assignmentsLoading = false;
      }
    },
    async updateAssignments(user, payload) {
      const projectId = this.$route.params.projectId;
      user.updating = true;
      try {
        await api.post(`/api/projects/${projectId}/update_assignments/`, {
          user_id: user.id,
          ...payload,
        });
        await this.fetchAssignments();
      } catch (error) {
        console.error("Error updating assignments:", error);
      } finally {
        user.updating = false;
      }
    },
    addAssignment(user) {
      if (!user.newDocumentId) return;
      if (user.newPageIds && user.newPageIds.length > 0) {
        this.updateAssignments(user, { add_page_ids: user.newPageIds });
      } else {
        this.updateAssignments(user, { add_document_ids: [user.newDocumentId] });
      }
    },
    removeAssignedDocument(user, docId) {
      this.updateAssignments(user, { remove_document_ids: [docId] });
    },
    removeAssignedPage(user, pageId) {
      this.updateAssignments(user, { remove_page_ids: [pageId] });
    },
    openImportDocumentDialog() {
      this.importDocumentDialog = true
    },
 async onImportDocuments({ baseURL, token, documents }) {
    const projectId = this.$route.params.projectId
    try {
      await Promise.all(
        documents.map(doc =>
          api.post(`/api/projects/${projectId}/import_document/`, {
            url: `${baseURL}/document/${doc.pk}/`,
            api_token: token,
          })
        )
      )
      this.$toast?.success?.(`Imported ${documents.length} document(s).`)
      await this.fetchDocuments()
    } catch (e) {
      console.error(e)
      this.$toast?.error?.('Failed to import one or more documents')
    }
  },

  async onImportDocument({ baseURL, token, document }) {
    const projectId = this.$route.params.projectId
    try {
      await api.post(`/api/projects/${projectId}/import_document/`, {
        url: `${baseURL}/document/${document.pk}/`,
        api_token: token,
      })
      this.$toast?.success?.('Document imported')
      await this.fetchDocuments()
    } catch (e) {
      console.error(e)
      this.$toast?.error?.('Failed to import document')
    }
  },
    toggleSelectAll() {
      // If everything is currently selected, unselect all. Otherwise, select all.
      if (this.isAllSelected) {
        this.selectedDocuments = [];
      } else {
        this.selectedDocuments = this.documents.map((doc) => doc.id);
      }
    },
    goToProjectDashboard() {
    const projectId = this.project.id;
    this.$router.push(`/projects/${projectId}/dashboard`);
  },
    async fetchProject() {
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.get(`/api/projects/${projectId}/`);
        this.project = response.data;
      } catch (error) {
        console.error("Error fetching project:", error);
      }
    },
    async fetchDocuments() {
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.get(`/api/projects/${projectId}/documents/`);
        this.documents = await Promise.all(
          response.data.map(async (document) => {
            const lineSummary = await this.fetchDocumentLineSummary(document.id);
            return { ...document, lineStatus: lineSummary };
          })
        );
      } catch (error) {
        console.error("Error fetching documents:", error);
      } finally {
        this.loading = false;
      }
    },
    async fetchDocumentLineSummary(documentId) {
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.get(`/api/projects/${projectId}/documents/${documentId}/line_summary/`);
        return {
          not_transcribed: response.data.line_status_summary.not_transcribed || 0,
          transcribed: response.data.line_status_summary.transcribed || 0,
          validated: response.data.line_status_summary.validated || 0,
          to_retranscribe: response.data.line_status_summary.to_retranscribe || 0,
        };
      } catch (error) {
        console.error(`Error fetching line summary for document ${documentId}:`, error);
        return {
          not_transcribed: 0,
          transcribed: 0,
          validated: 0,
          to_retranscribe: 0,
        };
      }
    },
    getStatusColor(status) {
      const colors = {
  not_transcribed: "#E57373", // Darker Red
  transcribed: "#FDD835",    // Darker Yellow
  validated: "#66BB6A",      // Darker Green
  to_retranscribe: "#42A5F5" // Darker Blue
};
      return colors[status] || "#E0E0E0"; // Default Gray
    },

    handleEdit() {
  if (this.selectedDocuments.length === 1) {
    const documentId = this.selectedDocuments[0];
    const document = this.documents.find((doc) => doc.id === documentId);
    this.openEditDocumentDialog(document); // Use existing method
  }
},
handleDelete() {
  if (this.selectedDocuments.length > 0) {
    if (confirm("Are you sure you want to delete the selected documents?")) {
      this.selectedDocuments.forEach((documentId) => {
        this.deleteDocument(documentId); // Use existing method
      });
      this.selectedDocuments = [];
    }
  }
},

    getStatusTooltip(status) {
    const tooltips = {
      not_transcribed: "not transcribed lines",
      transcribed: "transcribed lines",
      validated: "validated lines",
      to_retranscribe: "lines to retranscribe",
    };
    return tooltips[status] || "Unknown status";
  },
    goToDocument(documentId) {
      const projectId = this.project.id;
      this.$router.push(`/projects/${projectId}/documents/${documentId}`);
    },
    goToProjectsList() {
      this.$router.push("/projects");
    },
    openCreateDocumentDialog() {
      this.documentFormData = { name: "" };
      this.createDocumentDialog = true;
    },
    openEditDocumentDialog(document) {
      this.documentFormData = { ...document };
      this.editDocumentDialog = true;
    },
    closeDialogs() {
      this.createDocumentDialog = false;
      this.editDocumentDialog = false;
      this.documentFormData = { id: null, name: "" };
    },
    async createDocument() {
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.post(`/api/projects/${projectId}/documents/`, this.documentFormData);
        this.documents.push(response.data);
        this.closeDialogs();
      } catch (error) {
        console.error("Error creating document:", error);
      }
    },
    async updateDocument() {
      try {
        const projectId = this.$route.params.projectId;
        const response = await api.put(`/api/projects/${projectId}/documents/${this.documentFormData.id}/`, this.documentFormData);
        const index = this.documents.findIndex((doc) => doc.id === response.data.id);
        if (index !== -1) this.documents.splice(index, 1, response.data);
        this.closeDialogs();
      } catch (error) {
        console.error("Error updating document:", error);
      }
    },
    async deleteDocument(documentId) {
      if (confirm("Are you sure you want to delete this document?")) {
        try {
          const projectId = this.$route.params.projectId;
          await api.delete(`/api/projects/${projectId}/documents/${documentId}/`);
          this.documents = this.documents.filter((doc) => doc.id !== documentId);
        } catch (error) {
          console.error("Error deleting document:", error);
        }
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays === 0) {
        // Today -> show time
        return `Today ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffDays === 1) {
        return `Yesterday ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      } else if (diffDays < 7) {
        return `${date.toLocaleDateString([], { weekday: 'short' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
      }
      return date.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
    },
    toggleDateSort() {
      this.currentDateSort = this.currentDateSort === 'asc' ? 'desc' : 'asc';
      // Local sort since server-side is not specified
      const multiplier = this.currentDateSort === 'asc' ? 1 : -1;
      this.documents.sort((a, b) => (new Date(a.created_at) - new Date(b.created_at)) * multiplier);
    }
  },
};
</script>

<style scoped>
.back-btn {
  margin-left: 10px;
}

.toolbar-btn {
  margin-right: 10px;
}

.v-data-table {
  border-radius: 12px;
  overflow: hidden;
}

.v-data-table th {
  font-weight: bold;
  text-transform: uppercase;
  font-size: 14px;
}

.v-btn {
  margin: 0 4px;
}
.clickable-row {
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.clickable-row:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
.v-theme--DarkTheme .clickable-row:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.delete-btn {
  background-color: rgb(var(--v-theme-surface));
  border-radius: 50%;
  width: 30px;
  height: 30px;
}

.edit-btn {
  background-color: rgb(var(--v-theme-surface));
  border-radius: 50%;
  width: 30px;
  height: 30px;
}

/* New square action buttons */
.actions-cell {
  white-space: nowrap;
}
.action-btn {
  width: 32px !important;
  height: 32px !important;
  min-width: 32px !important;
  border-radius: 6px !important; /* square with slight rounding */
  box-shadow: none !important;
  transition: background-color 0.2s ease, transform 0.15s ease;
  margin: 0 2px;
}
.action-btn.edit:hover { background-color: #1976d2 !important; }
.action-btn.delete:hover { background-color: #d32f2f !important; }
.action-btn:active { transform: scale(0.94); }

/* Sortable header styling */
.sortable-header {
  display: inline-flex;
  align-items: center;
  cursor: pointer;
  user-select: none;
  font-weight: 600;
  letter-spacing: .5px;
}
.sortable-header:hover { opacity: 0.75; }
.sort-icon { opacity: 0.8; }

.status-rectangles {
  display: flex;
  justify-content: center; /* Center horizontally */
  align-items: center; /* Center vertically */
  gap: 4px;
}

.status-rectangle {
  width: 30px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 12px;
  font-weight: bold;
  border-radius: 4px;
  cursor: default;
}

.v-data-table >>> th {
  background-color: rgb(var(--v-theme-surface-variant));
  color: rgb(var(--v-theme-on-surface));
  font-weight: bold !important;
  font-size: 14px;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px; /* Adjust as necessary */
  color: var(--v-theme-on-background); /* Automatically adapts to theme */
  opacity: 0.7; /* Subtle faded effect */
}

.empty-message {
  font-size: 1.2rem;
  margin-top: 16px;
  color: inherit;
}

.source-btn {
  border-radius: 4px !important;
  min-width: 36px !important;
  width: 36px !important;
  height: 36px !important;
}

.member-item {
  border-radius: 12px;
  margin-bottom: 8px;
  background-color: rgba(var(--v-border-color), 0.04);
  transition: background-color 0.2s ease;
}
.member-item:hover {
  background-color: rgba(var(--v-border-color), 0.1);
}
.member-role-select {
  max-width: 180px;
}

/* Assignments overlay */
.assignment-user {
  border-radius: 12px;
}
.assignment-chips {
  min-height: 36px;
}
.assignment-add-select {
  max-width: 300px;
  min-width: 200px;
}
.legend {
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 12px;
  opacity: 0.8;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.legend-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}
.circle-image {
  object-fit: cover;
  border-radius: 50%;
  width: 100%;
  height: 100%;
  display: block;
}
</style>
